import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { EASE_EXPO } from './ease';

/**
 * 行マスク・スタガーリビール。
 * lines: (string | string[])[] — 配列要素はフレーズ単位の折返し防止スパンになる。
 * per="char" で英字見出しの文字単位スタガー（日本語では使わないこと）。
 * アクセシビリティ: 全文をaria-labelに持ち、アニメスパンはaria-hidden。
 */
export function MaskedLines({
  as: Tag = 'div',
  lines,
  className = '',
  lineClassName = '',
  per = 'line',
  delay = 0,
  stagger = 0.09,
  duration = 0.9,
  once = true,
  margin = '-12%',
}) {
  const reduce = useReducedMotion();
  const fullText = lines
    .map((l) => (Array.isArray(l) ? l.join('') : l))
    .join(' ');

  if (reduce) {
    return (
      <Tag className={className}>
        {lines.map((line, i) => (
          <span
            key={i}
            className={`block ${
              typeof lineClassName === 'function' ? lineClassName(i) : lineClassName
            }`}
          >
            {Array.isArray(line) ? line.join('') : line}
          </span>
        ))}
      </Tag>
    );
  }

  return (
    <Tag className={className}>
      <span className="sr-only">{fullText}</span>
      {lines.map((line, li) => {
        const phrases = Array.isArray(line) ? line : [line];
        const lc = typeof lineClassName === 'function' ? lineClassName(li) : lineClassName;
        return (
          <span
            key={li}
            aria-hidden="true"
            translate="no"
            className={`block overflow-hidden ${lc}`}
          >
            {per === 'char' ? (
              <CharLine
                text={phrases.join('')}
                delay={delay + li * stagger}
                duration={duration}
                once={once}
                margin={margin}
              />
            ) : (
              /* overflow-hiddenで完全に隠れた要素はIntersectionObserverが
                 交差扱いしないため、観測は非クリップの親側で行い子をvariantsで動かす */
              <motion.span
                className="block"
                initial="hidden"
                whileInView="show"
                viewport={{ once, margin }}
              >
                <motion.span
                  className="block will-change-transform"
                  variants={{
                    hidden: { y: '110%' },
                    show: {
                      y: '0%',
                      transition: { duration, ease: EASE_EXPO, delay: delay + li * stagger },
                    },
                  }}
                >
                  {phrases.map((p, pi) => (
                    <span key={pi} className="inline-block">
                      {p}
                    </span>
                  ))}
                </motion.span>
              </motion.span>
            )}
          </span>
        );
      })}
    </Tag>
  );
}

function CharLine({ text, delay, duration, once, margin }) {
  return (
    <motion.span
      className="block"
      initial="hidden"
      whileInView="show"
      viewport={{ once, margin }}
      variants={{
        show: { transition: { staggerChildren: 0.022, delayChildren: delay } },
      }}
    >
      {[...text].map((ch, i) => (
        <motion.span
          key={i}
          className="inline-block will-change-transform"
          variants={{
            hidden: { y: '112%' },
            show: { y: '0%', transition: { duration, ease: EASE_EXPO } },
          }}
        >
          {ch === ' ' ? ' ' : ch}
        </motion.span>
      ))}
    </motion.span>
  );
}

export default MaskedLines;
